import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { getMe, logout, getClassrooms, joinClassroom, getExercises, getBadges, getClassLeaderboard, getSchoolLeaderboard, getStudentObjectivesPerformance } from '../lib/api';
import { toast } from 'sonner';
import { 
  Keyboard, Plus, BookOpen, LogOut, 
  BarChart3, ChevronRight, CheckCircle, Clock, Target,
  Trophy, Medal, Star, Zap, Flame, Brain, Rocket, Award,
  Crown, Users, Globe, Shield, AlertTriangle
} from 'lucide-react';

const BADGE_ICONS = {
  rocket: Rocket,
  hand: Award,
  star: Star,
  target: Target,
  zap: Zap,
  brain: Brain,
  flame: Flame,
  calendar: CheckCircle,
  keyboard: Keyboard,
  trophy: Trophy
};

const BADGE_COLORS = {
  primary: 'bg-primary/20 text-primary border-primary/30',
  secondary: 'bg-secondary/20 text-secondary border-secondary/30',
  success: 'bg-neon-green/20 text-neon-green border-neon-green/30',
  warning: 'bg-neon-yellow/20 text-neon-yellow border-neon-yellow/30',
  error: 'bg-neon-red/20 text-neon-red border-neon-red/30',
  orange: 'bg-neon-orange/20 text-neon-orange border-neon-orange/30',
  pink: 'bg-neon-pink/20 text-neon-pink border-neon-pink/30',
  lime: 'bg-neon-lime/20 text-neon-lime border-neon-lime/30'
};

const StudentDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [classrooms, setClassrooms] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [badges, setBadges] = useState({ earned_count: 0, total_count: 0, badges: [] });
  const [classLeaderboard, setClassLeaderboard] = useState(null);
  const [schoolLeaderboard, setSchoolLeaderboard] = useState(null);
  const [objectivesData, setObjectivesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [classCode, setClassCode] = useState('');
  const [activeTab, setActiveTab] = useState('exercises');

  useEffect(() => {
    const fetchData = async () => {
      try {
        let currentUser = user;
        if (!currentUser) {
          const response = await getMe();
          if (response.data.role !== 'student') {
            navigate('/teacher');
            return;
          }
          setUser(response.data);
          currentUser = response.data;
        }
        const [classroomsRes, exercisesRes, badgesRes] = await Promise.all([
          getClassrooms(),
          getExercises(),
          getBadges()
        ]);
        setClassrooms(classroomsRes.data);
        setExercises(exercisesRes.data);
        setBadges(badgesRes.data);
        
        // Load objectives performance for this student
        try {
          const objectivesRes = await getStudentObjectivesPerformance(currentUser.user_id);
          setObjectivesData(objectivesRes.data);
        } catch (e) {
          console.log('Could not load objectives data');
        }
        
        // Load leaderboards
        if (classroomsRes.data.length > 0) {
          try {
            const classLbRes = await getClassLeaderboard(classroomsRes.data[0].classroom_id);
            setClassLeaderboard(classLbRes.data);
          } catch (e) {
            console.log('No class leaderboard');
          }
        }
        
        try {
          const schoolLbRes = await getSchoolLeaderboard();
          setSchoolLeaderboard(schoolLbRes.data);
        } catch (e) {
          console.log('No school leaderboard');
        }
        
      } catch {
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, navigate]);

  const handleJoinClass = async () => {
    if (!classCode) {
      toast.error('Please enter a class code');
      return;
    }
    setJoining(true);
    try {
      const response = await joinClassroom(classCode);
      toast.success(`Joined ${response.data.name}!`);
      setDialogOpen(false);
      setClassCode('');
      // Refresh data
      const [classroomsRes, exercisesRes] = await Promise.all([
        getClassrooms(),
        getExercises()
      ]);
      setClassrooms(classroomsRes.data);
      setExercises(exercisesRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join classroom');
    } finally {
      setJoining(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="flex items-center gap-1 text-status-success text-sm">
            <CheckCircle className="w-4 h-4" /> Completed
          </span>
        );
      case 'in_progress':
      case 'typing_complete':
        return (
          <span className="flex items-center gap-1 text-status-warning text-sm">
            <Clock className="w-4 h-4" /> In Progress
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-text-muted text-sm">
            <Target className="w-4 h-4" /> Not Started
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const completedCount = exercises.filter(e => e.user_status === 'completed').length;
  const avgAccuracy = exercises.length > 0 
    ? exercises.reduce((acc, e) => acc + (e.user_accuracy || 0), 0) / exercises.length 
    : 0;
  const earnedBadges = badges.badges.filter(b => b.earned);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-white/5 bg-background-paper/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
              <Keyboard className="w-5 h-5 text-secondary" />
            </div>
            <span className="font-heading text-xl font-bold">LitBlitz</span>
          </div>
          <div className="flex items-center gap-4">
            {user?.is_admin && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/admin')}
                data-testid="admin-btn"
                className="border-primary/50 text-primary hover:bg-primary/10"
              >
                <Shield className="w-4 h-4 mr-2" />
                Admin
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/progress')}
              data-testid="progress-btn"
              className="text-text-secondary hover:text-white"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              My Progress
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-text-secondary hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2" data-testid="student-welcome">
            Hello, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-text-secondary">Practice typing and improve your reading skills</p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="card-glass p-4" data-testid="stat-completed">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-status-success/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-status-success" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Completed</p>
                <p className="font-heading text-xl font-bold">{completedCount}</p>
              </div>
            </div>
          </Card>
          <Card className="card-glass p-4" data-testid="stat-accuracy">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Avg Accuracy</p>
                <p className="font-heading text-xl font-bold">{avgAccuracy.toFixed(0)}%</p>
              </div>
            </div>
          </Card>
          <Card className="card-glass p-4" data-testid="stat-badges">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-status-warning/20 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-status-warning" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Badges</p>
                <p className="font-heading text-xl font-bold">{badges.earned_count}/{badges.total_count}</p>
              </div>
            </div>
          </Card>
          <Card className="card-glass p-4" data-testid="stat-rank">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                <Crown className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">School Rank</p>
                <p className="font-heading text-xl font-bold">
                  #{schoolLeaderboard?.current_user?.rank || '-'}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList className="bg-background-paper border border-white/10">
              <TabsTrigger value="exercises" className="data-[state=active]:bg-primary data-[state=active]:text-black">
                <Keyboard className="w-4 h-4 mr-2" />
                Exercises
              </TabsTrigger>
              <TabsTrigger value="badges" className="data-[state=active]:bg-primary data-[state=active]:text-black">
                <Trophy className="w-4 h-4 mr-2" />
                Badges
              </TabsTrigger>
              <TabsTrigger value="leaderboard" className="data-[state=active]:bg-primary data-[state=active]:text-black">
                <Medal className="w-4 h-4 mr-2" />
                Leaderboard
              </TabsTrigger>
            </TabsList>
            
            {activeTab === 'exercises' && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-secondary hover:bg-secondary-hover text-white rounded-sm"
                    data-testid="join-class-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Join Class
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-background-paper border-white/10">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">Join a Classroom</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <label className="text-sm text-text-secondary mb-2 block">Class Code</label>
                      <Input
                        className="input-cyber font-mono text-center text-lg tracking-widest uppercase"
                        placeholder="XXXXXX"
                        maxLength={6}
                        value={classCode}
                        onChange={(e) => setClassCode(e.target.value.toUpperCase())}
                        data-testid="class-code-input"
                      />
                    </div>
                    <Button 
                      className="bg-secondary hover:bg-secondary-hover text-white w-full rounded-sm"
                      onClick={handleJoinClass}
                      disabled={joining}
                      data-testid="submit-join-btn"
                    >
                      {joining ? 'Joining...' : 'Join Classroom'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* Exercises Tab */}
          <TabsContent value="exercises" className="space-y-4">
            {classrooms.length === 0 ? (
              <Card className="card-glass p-12 text-center" data-testid="no-classes">
                <BookOpen className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="font-heading text-lg font-bold mb-2">No classes joined yet</h3>
                <p className="text-text-secondary mb-6">Ask your teacher for a class code to get started</p>
                <Button 
                  className="bg-secondary hover:bg-secondary-hover text-white rounded-sm"
                  onClick={() => setDialogOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Join Class
                </Button>
              </Card>
            ) : exercises.length === 0 ? (
              <Card className="card-glass p-12 text-center" data-testid="no-exercises">
                <Keyboard className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="font-heading text-lg font-bold mb-2">No exercises yet</h3>
                <p className="text-text-secondary">Your teacher hasn't assigned any exercises yet. Check back soon!</p>
              </Card>
            ) : (
              exercises.map((exercise) => (
                <Card 
                  key={exercise.exercise_id}
                  className="card-glass p-6 cursor-pointer group hover:border-primary/30 transition-colors"
                  onClick={() => {
                    if (exercise.user_status === 'completed') {
                      navigate(`/results/${exercise.exercise_id}/view`);
                    } else {
                      navigate(`/exercise/${exercise.exercise_id}`);
                    }
                  }}
                  data-testid={`exercise-card-${exercise.exercise_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                        <Keyboard className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-bold group-hover:text-primary transition-colors">
                          {exercise.title}
                        </h3>
                        <p className="text-text-secondary text-sm">
                          {exercise.objectives?.length > 1 
                            ? exercise.objectives.map(o => o.code).join(', ')
                            : exercise.objective_code} • Grade {exercise.grade_level} • {exercise.duration_minutes} min
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {getStatusBadge(exercise.user_status)}
                      {exercise.user_status === 'completed' && (
                        <div className="text-right">
                          <p className="text-text-secondary text-xs">Accuracy</p>
                          <p className="font-mono text-primary font-bold">{exercise.user_accuracy}%</p>
                        </div>
                      )}
                      <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-primary transition-colors" />
                    </div>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Badges Tab */}
          <TabsContent value="badges">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {badges.badges.map((badge) => {
                const IconComponent = BADGE_ICONS[badge.icon] || Star;
                const colorClass = BADGE_COLORS[badge.color] || BADGE_COLORS.primary;
                
                return (
                  <Card 
                    key={badge.id}
                    className={`card-glass p-4 text-center transition-all ${
                      badge.earned 
                        ? 'border-2 ' + colorClass.split(' ')[2] 
                        : 'opacity-40 grayscale'
                    }`}
                    data-testid={`badge-${badge.id}`}
                  >
                    <div className={`w-14 h-14 rounded-full ${colorClass.split(' ').slice(0, 2).join(' ')} flex items-center justify-center mx-auto mb-3`}>
                      <IconComponent className={`w-7 h-7 ${colorClass.split(' ')[1]}`} />
                    </div>
                    <h4 className="font-heading font-bold text-sm mb-1">{badge.name}</h4>
                    <p className="text-text-muted text-xs">{badge.description}</p>
                    {badge.earned && (
                      <p className="text-status-success text-xs mt-2 flex items-center justify-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Earned!
                      </p>
                    )}
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <Tabs defaultValue="school" className="space-y-4">
              <TabsList className="bg-background-paper border border-white/10">
                <TabsTrigger value="school" className="data-[state=active]:bg-secondary data-[state=active]:text-white">
                  <Globe className="w-4 h-4 mr-2" />
                  School
                </TabsTrigger>
                <TabsTrigger value="class" className="data-[state=active]:bg-secondary data-[state=active]:text-white">
                  <Users className="w-4 h-4 mr-2" />
                  My Class
                </TabsTrigger>
              </TabsList>

              {/* School Leaderboard */}
              <TabsContent value="school">
                {schoolLeaderboard?.leaderboard?.length > 0 ? (
                  <Card className="card-glass overflow-hidden">
                    <div className="p-4 border-b border-white/10">
                      <h3 className="font-heading font-bold flex items-center gap-2">
                        <Globe className="w-5 h-5 text-secondary" />
                        School-Wide Leaderboard
                      </h3>
                      <p className="text-text-muted text-sm">{schoolLeaderboard.total_students} students</p>
                    </div>
                    <div className="divide-y divide-white/5">
                      {schoolLeaderboard.leaderboard.slice(0, 20).map((entry) => (
                        <div 
                          key={entry.user_id}
                          className={`flex items-center gap-4 p-4 ${entry.is_current_user ? 'bg-primary/10' : ''}`}
                          data-testid={`lb-entry-${entry.user_id}`}
                        >
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                            entry.rank === 1 ? 'bg-status-warning text-black' :
                            entry.rank === 2 ? 'bg-gray-400 text-black' :
                            entry.rank === 3 ? 'bg-orange-600 text-white' :
                            'bg-white/10 text-text-secondary'
                          }`}>
                            {entry.rank <= 3 ? (
                              <Crown className="w-5 h-5" />
                            ) : (
                              entry.rank
                            )}
                          </div>
                          <div className="flex-1">
                            <p className={`font-medium ${entry.is_current_user ? 'text-primary' : ''}`}>
                              {entry.name} {entry.is_current_user && '(You)'}
                            </p>
                            <p className="text-text-muted text-xs">
                              {entry.exercises_completed} exercises • {entry.total_words} words
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-bold text-secondary">{entry.score}</p>
                            <p className="text-text-muted text-xs">points</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                ) : (
                  <Card className="card-glass p-12 text-center">
                    <Trophy className="w-12 h-12 text-text-muted mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-bold mb-2">No rankings yet</h3>
                    <p className="text-text-secondary">Complete exercises to appear on the leaderboard!</p>
                  </Card>
                )}
              </TabsContent>

              {/* Class Leaderboard */}
              <TabsContent value="class">
                {classLeaderboard?.leaderboard?.length > 0 ? (
                  <Card className="card-glass overflow-hidden">
                    <div className="p-4 border-b border-white/10">
                      <h3 className="font-heading font-bold flex items-center gap-2">
                        <Users className="w-5 h-5 text-primary" />
                        {classLeaderboard.classroom_name}
                      </h3>
                    </div>
                    <div className="divide-y divide-white/5">
                      {classLeaderboard.leaderboard.map((entry) => (
                        <div 
                          key={entry.user_id}
                          className={`flex items-center gap-4 p-4 ${entry.is_current_user ? 'bg-primary/10' : ''}`}
                        >
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                            entry.rank === 1 ? 'bg-status-warning text-black' :
                            entry.rank === 2 ? 'bg-gray-400 text-black' :
                            entry.rank === 3 ? 'bg-orange-600 text-white' :
                            'bg-white/10 text-text-secondary'
                          }`}>
                            {entry.rank <= 3 ? (
                              <Crown className="w-5 h-5" />
                            ) : (
                              entry.rank
                            )}
                          </div>
                          <div className="flex-1">
                            <p className={`font-medium ${entry.is_current_user ? 'text-primary' : ''}`}>
                              {entry.name} {entry.is_current_user && '(You)'}
                            </p>
                            <p className="text-text-muted text-xs">
                              Accuracy: {entry.avg_accuracy}% • WPM: {entry.avg_wpm}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-bold text-primary">{entry.score}</p>
                            <p className="text-text-muted text-xs">points</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                ) : (
                  <Card className="card-glass p-12 text-center">
                    <Users className="w-12 h-12 text-text-muted mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-bold mb-2">No class rankings yet</h3>
                    <p className="text-text-secondary">Join a class and complete exercises to compete!</p>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default StudentDashboard;
